import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ShortcutHint } from '@/src/renderer/components/ui/ShortcutHint'
import {
  getRewardById,
  getRewardIconUrl,
  getRewardPalette,
  rewardCta,
  type CatalogLanguage,
} from '@/src/renderer/lib/data/rewardCatalog'
import type { DoneMomentProps } from './DoneMoment.types'
import partyPopper from '@/src/renderer/assets/images/doneMomentPartyPopper.webp'
import { DoneMomentConfetti } from './confetti/DoneMomentConfetti'
import './DoneMoment.scss'

/**
 * **Der Geschafft-Moment** — das letzte Glied der Kernschleife. Eine kleine
 * Karte, die sagt: fertig, und jetzt ist kurz nichts.
 *
 * Sie ist **fast nur Bild und Belohnung**. „Geschafft." steht groß darüber, der
 * Ausgang klein darunter; das Auge soll auf der Belohnung landen, nicht auf dem
 * Knopf, der sie wegklickt. Das Leuchten in der Karte ist dasselbe Bild noch
 * einmal, stark weichgezeichnet — der Schein kommt also aus der Belohnung
 * selbst.
 *
 * **Ohne Belohnung** steht ein Partyhorn da, kein Namensknopf, und der Zuspruch
 * wechselt: „Atme kurz durch."
 *
 * **Sie bucht nichts** (CONTEXT.md, Glossar): anzeigen, wegklicken, fertig.
 * Beide Knöpfe tun dasselbe.
 *
 * Reine Props — keine Datenquelle, kein Radix. Wer die Aufgabe lädt und wie die
 * Karte schwebt, weiß die Hülle (`DoneMomentHost`).
 *
 * @example
 * <DoneMoment rewardId={task.rewardId} onClose={dismiss} />
 */
export const DoneMoment: React.FC<DoneMomentProps> = ({
  rewardId,
  onClose,
  Heading = 'h2',
  className,
}) => {
  const { t, i18n } = useTranslation()


  const reward = rewardId ? getRewardById(rewardId) : null
  const language = (i18n.language?.slice(0, 2) ?? 'de') as CatalogLanguage
  // 256 px: Das Bild steht hier frei bei 194 px, nicht im Squircle wie überall
  // sonst — die 64er wäre sichtbar weich. Ohne Belohnung das Partyhorn.
  const artwork = (reward && getRewardIconUrl(reward.id, 256)) || partyPopper
  const cta = reward ? rewardCta(reward, language) : ''

  /*
    **Die Farben bringt die Belohnung mit** (F-KS6): Grundton für Knopf, Ringe
    und Rahmen, Highlight für die Tönung des Schattens, Bildgrund für die Form
    unter dem Knopf. Als Variablen am Wurzelelement, weil die
    Werte aus Daten kommen — wie die `--bar-*` der Options-Leiste. Fehlt ein
    Wert, bleibt die Variable ungesetzt, und `globals.scss` springt ein.
  */
  /*
    Der durchsichtige Rand unter dem Bild, auf die 194px der Anzeige gerechnet —
    damit JEDES Bild gleich weit in den Knopf ragt (`DoneMoment.scss`).
  */
  const artBottom = reward?.artBottom != null
    ? { '--art-bottom': `${(reward.artBottom * 194) / 256}px` } as React.CSSProperties
    : undefined

  /*
    **Konfetti fällt mit dem Auftritt der Karte** — nicht beim Öffnen: Aus dem
    Fokus-Fenster ist das Hauptfenster anfangs noch versteckt, und die Karte
    tritt erst auf, wenn der Schleier halb steht. Ohne Belohnung halb so viel;
    der Moment ist dort bewusst ruhiger.
  */
  const [confettiStarted, setConfettiStarted] = useState(false)
  const onAnimationStart = (event: React.AnimationEvent<HTMLDivElement>) => {
    if (event.animationName === 'done-moment-rise' && event.target === event.currentTarget) setConfettiStarted(true)
  }

  const palette = getRewardPalette(reward)
  const colors = {
    ...(palette.base ? { '--done-moment-deep': palette.base } : {}),
    ...(palette.highlight ? { '--done-moment-highlight': palette.highlight } : {}),
    ...(palette.glow ? { '--done-moment-glow': palette.glow } : {}),
  } as React.CSSProperties

  const classes = [
    'done-moment',
    reward ? '' : 'done-moment--plain',
    className ?? '',
  ].filter(Boolean).join(' ')

  return (
    <div className={classes} style={colors} onAnimationStart={onAnimationStart}>
      <DoneMomentConfetti started={confettiStarted} count={reward ? 70 : 35} />
      {/* Die Form im Bildgrund unter dem Knopf, weichgezeichnet (Figma 1818-50986). */}
      <span className="done-moment__glow-field" aria-hidden="true" />

      <div className="done-moment__head">
        <Heading className="done-moment__headline">{t('doneMoment.title')}</Heading>
        <p className="done-moment__encouragement">
          {t(reward ? 'doneMoment.withReward' : 'doneMoment.withoutReward')}
        </p>
      </div>

      <div className="done-moment__body">
        <div className="done-moment__reward">
          {/* Die Grafik noch einmal, rund zugeschnitten und weichgezeichnet —
              der Schein kommt aus der Belohnung selbst, genau hinter dem Bild. */}
          <div className="done-moment__glow" aria-hidden="true">
            <img src={artwork} alt="" />
          </div>
          <img
            className="done-moment__artwork"
            src={artwork}
            alt=""
            width={194}
            height={194}
            style={artBottom}
          />

          {reward && (
            /* Zwei Ringe wie im Entwurf, und das Fokus-Fenster überlappt das Bild von
               unten — darum sitzt sie IN der Bildspalte und nicht darunter. */
            <span className="done-moment__ring">
              <span className="done-moment__ring-inner">
                <button type="button" className="done-moment__claim" onClick={onClose}>
                  {cta}
                </button>
              </span>
            </span>
          )}
        </div>

        {/* Der Fokus liegt hier, damit ↵ ihn auslöst — das Tastenplättchen
            daneben soll die Wahrheit sagen. */}
        <button
          type="button"
          className="done-moment__continue"
          onClick={onClose}
          autoFocus
        >
          {t('doneMoment.continue')}
          <ShortcutHint>↵</ShortcutHint>
        </button>
      </div>
    </div>
  )
}
